interface EmailModeSettingsSubTabProps {
  useSharedMailbox: boolean;
  onUseSharedMailboxChange: (value: boolean) => void;
  officialEmailDraft: string;
  onOfficialEmailDraftChange: (value: string) => void;
  onSave: () => void;
  isSaving: boolean;
  unchanged: boolean;
}

export function EmailModeSettingsSubTab({
  useSharedMailbox,
  onUseSharedMailboxChange,
  officialEmailDraft,
  onOfficialEmailDraftChange,
  onSave,
  isSaving,
  unchanged,
}: EmailModeSettingsSubTabProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-800">Email mode</h3>
        <p className="mt-1 text-sm text-gray-500">
          Choose how your organization syncs and shares collection emails.
        </p>
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50/80 p-4">
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium text-gray-800">
            Use shared collections mailbox
          </p>
          {useSharedMailbox ? (
            <p className="text-sm text-gray-600">
              One collections mailbox for the whole team. Everyone can view
              messages and send emails from the official address.
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              Each team member syncs their own inbox. Emails are private to that
              user.
            </p>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={useSharedMailbox}
          disabled={isSaving}
          onClick={() => onUseSharedMailboxChange(!useSharedMailbox)}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 ${
            useSharedMailbox ? "bg-indigo-600" : "bg-gray-200"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              useSharedMailbox ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {useSharedMailbox && (
        <div className="space-y-2">
          <label
            htmlFor="official-outgoing-email"
            className="block text-sm font-medium text-gray-700"
          >
            Official outgoing email
          </label>
          <input
            id="official-outgoing-email"
            type="email"
            value={officialEmailDraft}
            onChange={(e) => onOfficialEmailDraftChange(e.target.value)}
            disabled={isSaving}
            placeholder="collections@yourcompany.com"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
          />
          <p className="text-xs text-gray-500">
            Must match a team member&apos;s login email. That user connects
            Gmail or Outlook to sync and send.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving || unchanged}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save email mode"}
        </button>
      </div>
    </div>
  );
}
